import { z } from 'zod';

/**
 * Email validation schema
 * Validates RFC 5322 compliant email addresses
 */
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address');

/**
 * US Phone number validation schema
 * Supports formats: (555) 123-4567, 555-123-4567, 555.123.4567, 5551234567, 555 123 4567
 * Phone is optional, but if provided must be valid
 */
export const phoneSchema = z
  .string()
  .optional()
  .refine(
    (value) => {
      if (!value || value.trim() === '') return true; // Optional - empty is valid
      // Remove all non-digit characters and check if we have exactly 10 digits
      const digitsOnly = value.replace(/\D/g, '');
      return digitsOnly.length === 10;
    },
    { message: 'Please enter a valid 10-digit US phone number' }
  );

/**
 * Contact form validation schema
 */
export const contactSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: emailSchema,
  phone: phoneSchema,
  title: z.string().optional(),
  type: z.string().optional(),
});

/**
 * Contact info schema for AddClientModal
 */
export const contactInfoSchema = z.object({
  name: z.string().optional(),
  email: z.string().optional().refine(
    (value) => {
      if (!value || value.trim() === '') return true; // Optional - empty is valid
      // Basic email pattern
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(value);
    },
    { message: 'Please enter a valid email address' }
  ),
  phone: phoneSchema,
});

/**
 * Validates an email address
 * @returns Error message if invalid, undefined if valid
 */
export function validateEmail(email: string): string | undefined {
  if (!email || email.trim() === '') {
    return 'Email is required';
  }
  
  // Standard email regex pattern (RFC 5322 simplified)
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailPattern.test(email)) {
    return 'Please enter a valid email address';
  }
  
  return undefined;
}

/**
 * Validates a US phone number
 * @returns Error message if invalid, undefined if valid (or empty, since phone is optional)
 */
export function validatePhone(phone: string): string | undefined {
  if (!phone || phone.trim() === '') {
    return undefined; // Phone is optional
  }
  
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  if (digitsOnly.length !== 10) {
    return 'Please enter a valid 10-digit US phone number';
  }
  
  return undefined;
}

/**
 * Formats a phone number as user types
 * Converts input to (XXX) XXX-XXXX format
 */
export function formatPhoneNumber(value: string): string {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limited = digitsOnly.slice(0, 10);
  
  // Format based on number of digits
  if (limited.length === 0) {
    return '';
  } else if (limited.length <= 3) {
    return `(${limited}`;
  } else if (limited.length <= 6) {
    return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  } else {
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  }
}

/**
 * Checks if a string is a valid email format
 */
export function isValidEmail(email: string): boolean {
  return validateEmail(email) === undefined;
}

/**
 * Checks if a string is a valid phone format (or empty)
 */
export function isValidPhone(phone: string): boolean {
  return validatePhone(phone) === undefined;
}

export type ContactFormData = z.infer<typeof contactSchema>;
export type ContactInfoData = z.infer<typeof contactInfoSchema>;
